import { useCallback, useEffect, useRef, useState } from 'react';
import { createCancelSource, fetchVersions, isCancel } from '../api';

/**
 * Version-history read for the CST admin drawer's Versions tab. Modeled on
 * `useCreativeRequests`'s cancel-token/mounted-ref/req-id-guard pattern, but
 * read-only — no mutation actions, just `{ versions, loading, error, reload }`.
 *
 * `fetchVersions` resolves to `{ data: Version[] }` (the outer `{code, data}`
 * envelope is already stripped by `api.js`'s `unwrap`) — this hook unwraps
 * that inner `data` array so callers get a plain list.
 */
const useCreativeVersions = (bscId, { enabled = true } = {}) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const mountedRef = useRef(true);
  const reqRef = useRef(0);
  const sourceRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (sourceRef.current) sourceRef.current.cancel('component unmounted');
    };
  }, []);

  const load = useCallback(async () => {
    if (!bscId) return;
    const reqId = reqRef.current + 1;
    reqRef.current = reqId;
    if (sourceRef.current) sourceRef.current.cancel('superseded');
    const source = createCancelSource();
    sourceRef.current = source;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchVersions(bscId, source.token);
      if (!mountedRef.current || reqId !== reqRef.current) return;
      const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      setVersions(list);
    } catch (err) {
      if (isCancel(err)) return;
      if (!mountedRef.current || reqId !== reqRef.current) return;
      setError(err?.message || 'Unable to load the version history.');
    } finally {
      if (mountedRef.current && reqId === reqRef.current) setLoading(false);
    }
  }, [bscId]);

  useEffect(() => {
    if (!enabled || !bscId) {
      setVersions([]);
      return undefined;
    }
    load();
    return () => {
      if (sourceRef.current) sourceRef.current.cancel('creative changed');
    };
  }, [enabled, bscId, load]);

  return { versions, loading, error, reload: load };
};

export default useCreativeVersions;
